/**
 * The link somebody arrived on, held until the app is ready for it.
 *
 * SMALL, AND DELIBERATELY SO. It imports `./share.js` and nothing else, which
 * is what lets both ends of the app ask whether a link is waiting without a
 * cycle: `src/share-open.js` applies it, and `src/app-state.js` asks because
 * the answer changes the onboarding rule. Putting the ref in share-open.js
 * meant app-state importing the module that imports app-state.
 *
 * It runs AT MODULE LOAD, on purpose. `main.js` emits its `route` event before
 * `App.vue` mounts, so a listener registered in a component would miss the
 * cold start entirely; reading `location` here happens before the router's
 * first navigation and therefore before anything can have decided otherwise.
 *
 * ── A SHARE LINK IS ITS OWN WELCOME ──────────────────────────────────────────
 *
 * The welcome asks five questions, and the first two are which city and which
 * station. A link answers both. So somebody opening the app for the first time
 * from a link sent to them lands on the board they were sent, and is asked the
 * rest another day — see `onboardingNeeded` in app-state.js, which reads
 * `sharePending`. Five questions in front of a board somebody has been sent is
 * how a shared link becomes a link nobody follows twice.
 *
 * An install that is ALREADY onboarded is untouched by any of this: the flag
 * is already set and the welcome was never going to run.
 */

import { computed, ref } from 'vue';
import { parseShareUrl } from './share.js';

const pending = ref(null);

/** Is a link waiting to be applied? */
export const sharePending = computed(() => pending.value !== null);

/** Accept a URL that may carry a link. True if it did. */
export function offerShare(href) {
  const desc = parseShareUrl(href);
  if (!desc) return false;
  pending.value = desc;
  return true;
}

/** The waiting descriptor, cleared. Null when nobody sent anything. */
export function takePendingShare() {
  const desc = pending.value;
  pending.value = null;
  return desc;
}

/** Tests only. */
export function peekPendingShare() {
  return pending.value;
}

/**
 * Read the token off the address bar and take it out of the address bar.
 *
 * TIDIED ONCE, immediately. `/s/<token>` becomes `/app` (or `/demo`, which is
 * how the demo honours a link), so a refresh an hour later opens the
 * recipient's own board rather than re-applying a board they have finished
 * with. The app navigates through a memory history, so the address bar is
 * decoration from this moment on and there is nothing left to keep in sync.
 *
 * In a try/catch that covers every way this can fail: no `location` under the
 * test runner, no `history` in an old WebView, a `replaceState` refused by a
 * sandbox. None of those is a reason for the app not to boot, and a link that
 * cannot be read is simply not a link.
 */
function readFromLocation() {
  try {
    if (typeof location === 'undefined') return;
    if (!offerShare(location.href)) return;
    const demo = /^\/(app\/)?demo(\/|$)/.test(location.pathname);
    window.history.replaceState(null, '', `${demo ? '/demo' : '/app'}${location.hash}`);
  } catch {
    /* no address bar to read, or none to rewrite. Either way, no link. */
  }
}

readFromLocation();
